import React, { useEffect, useState } from 'react'
import { Resizable } from 'react-resizable'
import { useResponsiveSizings } from '../../sharedComponents/src/hooks/useResponsiveSizings'
import { sizings } from './AppLayout'

export const ResizableBoardWrapper: React.FC<{
    children: React.ReactElement
    parentRef: React.RefObject<HTMLDivElement>
    childrenRef: React.RefObject<HTMLDivElement>
}> = ({ children, parentRef, childrenRef }) => {
    const BOARD_OFFSET = 14
    let storedBoardSize: string | null = null
    try {
        storedBoardSize = localStorage.getItem('boardSize')
    } catch {
        // storage can throw (Firefox while the page unloads, blocked storage): use the default size
    }
    const boardSize = parseInt(storedBoardSize || '2000') + BOARD_OFFSET

    const {
        resolution: { isMobile },
    } = useResponsiveSizings()

    const [{ height, width }, setSize] = useState({
        height: Math.max(boardSize, sizings.minBoardSize),
        width: Math.max(boardSize, sizings.minBoardSize),
    })

    useEffect(() => {
        if (!isMobile) {
            updateSize(
                {
                    width: Math.max(boardSize, sizings.minBoardSize),
                    height: Math.max(boardSize, sizings.minBoardSize),
                },
                'auto',
            )
        }
    }, [isMobile])

    useEffect(() => {
        !isMobile && addEventListener('resize', handleWindowResize)
        return () => {
            removeEventListener('resize', handleWindowResize)
        }
    }, [isMobile])

    const updateSize = (size: { width: number; height: number }, mode: 'auto' | 'manual') => {
        if (parentRef.current && childrenRef.current) {
            const maxWidth = parentRef.current?.scrollWidth - sizings.rightPanelMax - sizings.margin
            let parentWidthOrHeight = Math.min(parentRef.current.clientHeight, maxWidth)
            let isManualResizeMode = false
            try {
                isManualResizeMode = localStorage.getItem('resizeMode') === 'manual'
            } catch {}

            if (mode === 'manual') {
                localStorage.setItem('resizeMode', mode)
            }

            setSize((prev) => {
                let newSize = {
                    width: Math.round(Math.min(size.width || prev.width, parentWidthOrHeight)),
                    height: Math.round(Math.min(size.height || prev.height, parentWidthOrHeight)),
                }

                if (!isManualResizeMode && mode === 'auto') {
                    newSize = {
                        width: Math.round(parentWidthOrHeight),
                        height: Math.round(parentWidthOrHeight),
                    }
                }

                // window is downsizing or manual resize full container
                if (newSize.width === parentWidthOrHeight || (newSize.width < prev.width && mode === 'auto')) {
                    localStorage.setItem('resizeMode', 'auto')
                }

                localStorage.setItem('boardSize', `${newSize.width - BOARD_OFFSET}`)

                if (childrenRef.current) {
                    childrenRef.current.style.width = `${newSize.width}px`
                    childrenRef.current.style.height = `${newSize.width}px`
                }

                return { width: newSize.width, height: newSize.width }
            })
        }
    }

    const handleWindowResize = () => {
        updateSize(
            {
                width: 0,
                height: 0,
            },
            'auto',
        )
    }

    const onResize = (event, { node, size, handle }) => {
        updateSize(size, 'manual')
    }

    if (isMobile) {
        return children
    }

    return (
        <Resizable
            height={height}
            width={width}
            onResize={onResize}
            lockAspectRatio={true}
            minConstraints={[sizings.minBoardSize, sizings.minBoardSize]}
            resizeHandles={['sw', 'se', 'nw', 'ne', 'w', 'e', 'n', 's']}
        >
            {children}
        </Resizable>
    )
}
